import {
  PhonicsLessonGroup,
  PhonicsLesson,
  PhonicsActivity,
} from '@/api/types';

export const getLessonByIdFromGroups = (
  lessonId: string,
  lessonGroups: Array<PhonicsLessonGroup>,
) => {
  for (const yearGroup of lessonGroups) {
    for (const weekGroup of yearGroup.children || []) {
      const lesson = weekGroup.lessons?.find(
        (lesson) => lesson.id === lessonId,
      );

      if (lesson) {
        return lesson;
      }
    }
  }

  return undefined;
};

export const getLessonByActivityFromGroups = (
  activity: PhonicsActivity,
  lessonGroups: Array<PhonicsLessonGroup>,
) => {
  for (const yearGroup of lessonGroups) {
    for (const weekGroup of yearGroup.children || []) {
      for (const lesson of weekGroup.lessons || []) {
        if (
          lesson.activities.find(
            (lessonActivity) => lessonActivity.id === activity.id,
          )
        ) {
          return lesson;
        }
      }
    }
  }

  return undefined;
};

export const getActivity = (
  id: string,
  lessonsByGroups: Array<PhonicsLessonGroup>,
) =>
  lessonsByGroups
    .flatMap((yearGroup) =>
      yearGroup.children?.flatMap((weekGroup) =>
        weekGroup.lessons?.flatMap((lesson) =>
          lesson.activities.find((activity) => activity.id === id),
        ),
      ),
    )
    .find((activity) => !!activity);

export const getParentGroupByIdFromGroups = (
  groupId: string,
  lessonGroups: Array<PhonicsLessonGroup>,
) =>
  lessonGroups.find((yearGroup) =>
    yearGroup.children?.find((weekGroup) => weekGroup.id === groupId),
  );

export const LETTER_SET_LESSON_RANGES = [
  { min: 1, max: 8 }, // Letter set 1: s, a, t, p, i, n
  { min: 9, max: 16 }, // Letter set 2: c, e, h, r, m, d
  { min: 17, max: 24 }, // Letter set 3: g, o, u, l, f, b
] as const;

export const getLessonsByLetterSetIndex = (
  letterSetIndex: number,
  lessonsByGroups: Array<PhonicsLessonGroup>,
): Array<PhonicsLesson> => {
  const range = LETTER_SET_LESSON_RANGES[letterSetIndex];
  if (!range) {
    return [];
  }

  return lessonsByGroups.flatMap((yearGroup) =>
    !yearGroup.children
      ? []
      : yearGroup.children.flatMap((weekGroup) =>
          !weekGroup.lessons
            ? []
            : weekGroup.lessons.filter(
                (lesson) =>
                  lesson.number >= range.min && lesson.number <= range.max,
              ),
        ),
  );
};

export const getLessonsBySymbols = (
  symbols: {
    sounds?: Array<string>;
    patterns?: Array<string>;
  },
  lessonsByGroups: Array<PhonicsLessonGroup>,
): Array<PhonicsLesson> =>
  lessonsByGroups.flatMap((yearGroup) =>
    !yearGroup.children
      ? []
      : yearGroup.children.flatMap((weekGroup) =>
          !weekGroup.lessons
            ? []
            : weekGroup.lessons.filter((lesson) => {
                const hasSounds = lesson.sounds && lesson.sounds.length > 0;
                const hasPatterns =
                  lesson.patterns && lesson.patterns.length > 0;

                // Lesson must have at least sounds or patterns
                if (!hasSounds && !hasPatterns) {
                  return false;
                }

                const allSoundsMatch =
                  !hasSounds ||
                  (lesson.sounds?.every((soundObj) =>
                    symbols?.sounds?.includes(soundObj.name),
                  ) ??
                    false);

                const allPatternsMatch =
                  !hasPatterns ||
                  (lesson.patterns?.every((patternObj) =>
                    symbols?.patterns?.includes(patternObj.name),
                  ) ??
                    false);

                return allSoundsMatch && allPatternsMatch;
              }),
        ),
  );
